import type { AgentId } from '@developerz/domain';
import { pinFace } from '../components/ModelPicker';
import { t } from '../i18n';
import { AGENT_CARD_ORDER, AGENT_ROUTE_PURPOSE, orgRouteFor, pinTriggerValue } from './agents';
import { type AgentsPreviewWire, previewEntryFor } from './agents-preview';
import type { RouteWire } from './llmRoutes';

// The Fleet page's AI roles and models section (#6788, prototype `ROLES` /
// `rolesSection`), pure. Each cell's chip and the Default line read EXACTLY what the
// Agents page reads, through its own fold:
//
//   - the order is `AGENT_CARD_ORDER`, the Agents page's grid, less the cards the
//     prototype's nine roles do not name (`ROLE_COPY` is partial on purpose: the
//     intake screen, #6951, landed after the prototype and has no copy here yet);
//   - a role's pin is its `AGENT_ROUTE_PURPOSE` org route (`orgRouteFor`,
//     `pinTriggerValue`), so DevOps reads the Coder's model as the roster says;
//   - its preview pick is `previewEntryFor`, the preview's OWN map
//     (`AGENT_PREVIEW_PURPOSE`), never the pin-purpose map: Triage, Escalation and
//     Default have no preview entry;
//   - the chip is `pinFace(...).primary`, the Agents card trigger's face.
//
// ONE BRANCH IS OURS, AHEAD OF `pinFace`: a routes read that is not in (`null`:
// loading or failed) reads "-" on every chip, because `pinFace` with no pin says
// "Automatic", and that is a claim that no pin exists. `pinFace` never yields "-".
//
// No machine count: "On N of M machines" waits for #6757 to serve each machine's
// effective roles, so a cell carries no count field at all.
//
// Names and jobs are the prototype's copy, keyed here as literals so the i18n
// coverage and Fleet copy guards read them off this file.

/** A role's name and one-line job, the prototype's copy (`prototype.html` `ROLES`). */
const ROLE_COPY: Readonly<
  Partial<Record<AgentId, { readonly name: string; readonly job: string }>>
> = {
  coder: { name: 'fleet.roles.name_coder', job: 'fleet.roles.job_coder' },
  reviewer: { name: 'fleet.roles.name_reviewer', job: 'fleet.roles.job_reviewer' },
  ci_fixer: { name: 'fleet.roles.name_ci_fixer', job: 'fleet.roles.job_ci_fixer' },
  planner: { name: 'fleet.roles.name_planner', job: 'fleet.roles.job_planner' },
  scout: { name: 'fleet.roles.name_scout', job: 'fleet.roles.job_scout' },
  triage: { name: 'fleet.roles.name_triage', job: 'fleet.roles.job_triage' },
  devops: { name: 'fleet.roles.name_devops', job: 'fleet.roles.job_devops' },
  gardener: { name: 'fleet.roles.name_gardener', job: 'fleet.roles.job_gardener' },
  escalation: { name: 'fleet.roles.name_escalation', job: 'fleet.roles.job_escalation' },
};

/** The section's roles: the Agents page's order, filtered to the prototype's nine. */
export const ROLE_ORDER: readonly AgentId[] = AGENT_CARD_ORDER.filter(
  (agent) => ROLE_COPY[agent] !== undefined,
);

/** One role cell: the name and job keys, and the chip text. */
export type RoleCell = {
  readonly agent: AgentId;
  readonly name: string;
  readonly job: string;
  readonly chip: string;
};

/** The Default line: its chip, and whether it reads "Automatic" (the sentence shows then). */
export type DefaultLine = { readonly chip: string; readonly automatic: boolean };

/** The chip a routes read that is not in shows: not reported. */
const notReported = (): string => t('fleet.machines.not_reported');

/**
 * The nine cells, in the Agents page's order. `routes` is `GET /v1/llm-routes`
 * (`null` while it is not in, or failed); `preview` is `GET /v1/llm-routes/preview`
 * (`undefined` while it is not in, or failed: only the "→ model" part drops).
 */
export function roleCells(
  routes: readonly RouteWire[] | null,
  preview: AgentsPreviewWire | undefined,
): readonly RoleCell[] {
  return ROLE_ORDER.flatMap((agent) => {
    const copy = ROLE_COPY[agent];
    if (copy === undefined) return [];
    const { name, job } = copy;
    if (routes === null) return [{ agent, name, job, chip: notReported() }];
    const purpose = AGENT_ROUTE_PURPOSE[agent];
    const face = pinFace(orgRouteFor(routes, purpose), pinTriggerValue(routes, purpose), {
      previewPick: previewEntryFor(preview?.purposes, agent)?.pick?.model,
    });
    return [{ agent, name, job, chip: face.primary }];
  });
}

/**
 * The Default line, as the Agents page's Default card reads it: `pinFace` for the
 * `default` purpose with NO preview pick (the preview never answers for default,
 * `PREVIEW_PURPOSES`), so it takes no preview at all. Unpinned reads "Automatic"; a
 * routes read not in reads "-".
 */
export function defaultLine(routes: readonly RouteWire[] | null): DefaultLine {
  if (routes === null) return { chip: notReported(), automatic: false };
  const purpose = AGENT_ROUTE_PURPOSE.default;
  const pin = pinTriggerValue(routes, purpose);
  return { chip: pinFace(orgRouteFor(routes, purpose), pin).primary, automatic: pin === null };
}
